import { PREVIEW_GATEWAY_PATH_STORAGE_KEY as LS_KEY } from "utils/localStorage";
import { notificationStore } from "stores/notificationStore";

export interface Preview {
  pr: number;
  name: string;
  path: string;
  branch: string;
}

// Selected preview gateway's path prefix (e.g. "/api/preview/pr-1880"), or null
// for the live gateway. Read by the Apollo fetch; observed by the picker panel.
class PreviewGatewayStore {
  private path = read();
  private listeners = new Set<() => void>();

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  getPath = (): string | null => this.path;

  getPr = (): string | null =>
    this.path?.match(/^\/api\/preview\/(pr-\d+)$/)?.[1] ?? null;

  clearStaleSelection = async () => {
    if (!this.path) return; // prod never has a selection - instant return, no fetch
    const res = await fetch(
      `${import.meta.env.VITE_API_HOSTNAME || ""}/api/preview-gateways`,
      { credentials: "include" },
    ).catch(() => null);
    const body = res?.ok ? await res.json().catch(() => null) : null;
    const previews: Preview[] = Array.isArray(body) ? body : [];
    if (previews.some((p) => p.path === this.path)) return;
    notificationStore.showErrorToaster(
      `Preview ${this.getPr()} is no longer available — switched to the live gateway.`,
    );
    this.setPath(null);
  };

  setPath = (path: string | null) => {
    this.path = path;
    try {
      if (path) localStorage.setItem(LS_KEY, path);
      else localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
    this.listeners.forEach((cb) => cb());
  };
}

function read(): string | null {
  try {
    return localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}

export const previewGatewayStore = new PreviewGatewayStore();

// The API clients wait for this before they send a request, so that they do
// not use a stale preview gateway.
export const previewGatewayReady = previewGatewayStore.clearStaleSelection();
